import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconChevronRight,
  IconGripVertical,
  IconPhone,
  IconSend,
  IconX,
} from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { FriendLink } from "../../shared/types";
import type { DirectMessage, FriendPresence, Session } from "../lib/api";
import { fetchDMConversation, sendDM } from "../lib/api";

const PANEL_WIDTH = 320;
const PANEL_HEIGHT = 440;

function clampPosition(x: number, y: number): { x: number; y: number } {
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
  const maxY = Math.max(margin, window.innerHeight - PANEL_HEIGHT - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function initialPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 16, y: 200 };
  return clampPosition(
    Math.round(window.innerWidth / 2 - PANEL_WIDTH / 2),
    Math.round(window.innerHeight / 2 - PANEL_HEIGHT / 2)
  );
}

interface DmNotification {
  fromAddress: string;
  fromName: string;
  text?: string;
  seq: number;
}

interface Props {
  session: Session;
  acceptedFriends: FriendLink[];
  pendingFriends: FriendLink[];
  friendsOnline: FriendPresence[];
  unreadPerFriend: Record<string, number>;
  lastDmNotification: DmNotification | null;
  onAcceptFriend: (friend: FriendLink) => Promise<void>;
  onVisitRoom: (roomId: string) => void;
  onUnreadChange: () => void;
  onClose: () => void;
}

export function FriendsPhonePanel({
  session,
  acceptedFriends,
  pendingFriends,
  friendsOnline,
  unreadPerFriend,
  lastDmNotification,
  onAcceptFriend,
  onVisitRoom,
  onUnreadChange,
  onClose,
}: Props) {
  const [position, setPosition] = useState(initialPosition);
  const [activeChat, setActiveChat] = useState<{
    address: string;
    name: string;
  } | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [dmText, setDmText] = useState("");
  const [sending, setSending] = useState(false);
  const [acceptingFriendKey, setAcceptingFriendKey] = useState<string | null>(
    null
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Drag state
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setPosition(clampPosition(state.originX + dx, state.originY + dy));
  }, []);

  const onDragEnd = useCallback(
    function handleDragEnd() {
      stopDragging();
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    },
    [onDragMove, stopDragging]
  );

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
      stopDragging();
    };
  }, [onDragEnd, onDragMove, stopDragging]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  };

  // Build presence map
  const presenceMap = new Map<string, FriendPresence>();
  for (const f of friendsOnline) {
    presenceMap.set(f.address, f);
  }

  // Sort friends: online first
  const sortedFriends = [...acceptedFriends].sort((a, b) => {
    const aAddr =
      a.userAddress === session.address ? a.friendAddress : a.userAddress;
    const bAddr =
      b.userAddress === session.address ? b.friendAddress : b.userAddress;
    const aOnline = presenceMap.has(aAddr) ? 0 : 1;
    const bOnline = presenceMap.has(bAddr) ? 0 : 1;
    return aOnline - bOnline;
  });

  const incomingPendingFriends = pendingFriends.filter(
    (friend) =>
      friend.status === "pending" &&
      friend.friendAddress.toLowerCase() === session.address.toLowerCase()
  );

  // Open conversation
  const openConversation = useCallback(
    async (address: string, name: string) => {
      setActiveChat({ address, name });
      setLoadingMessages(true);
      setMessages([]);
      setDmText("");
      try {
        const msgs = await fetchDMConversation(session, address);
        setMessages(msgs);
        onUnreadChange();
      } catch {
        // ignore
      } finally {
        setLoadingMessages(false);
      }
    },
    [session, onUnreadChange]
  );

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector(
        "[data-radix-scroll-area-viewport], .mantine-ScrollArea-viewport"
      );
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }
  }, [messages]);

  // Instant append when a DM notification arrives for the active conversation
  useEffect(() => {
    if (
      !lastDmNotification?.text ||
      !activeChat ||
      lastDmNotification.fromAddress !== activeChat.address
    )
      return;
    setMessages((prev) => [
      ...prev,
      {
        id: `ws-${lastDmNotification.seq}`,
        senderAddress: lastDmNotification.fromAddress,
        text: lastDmNotification.text!,
        createdAt: new Date().toISOString(),
      },
    ]);
  }, [lastDmNotification?.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for new messages while conversation is open
  useEffect(() => {
    if (!activeChat) return;
    const timer = window.setInterval(async () => {
      try {
        const msgs = await fetchDMConversation(session, activeChat.address);
        setMessages(msgs);
        onUnreadChange();
      } catch {
        // ignore
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [activeChat, session, onUnreadChange]);

  const handleSend = async () => {
    if (!activeChat || !dmText.trim() || sending) return;
    const text = dmText.trim();
    setSending(true);
    setDmText("");
    try {
      await sendDM(session, activeChat.address, text);
      // Optimistic add
      setMessages((prev) => [
        ...prev,
        {
          id: `temp-${Date.now()}`,
          senderAddress: session.address,
          text,
          createdAt: new Date().toISOString(),
        },
      ]);
    } catch {
      setDmText(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper
      radius="md"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        onPointerDown={handleHeaderPointerDown}
        style={{
          touchAction: "none",
          cursor: "grab",
          padding: "8px 12px",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          userSelect: "none",
          flexShrink: 0,
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={14} color="rgba(0,0,0,0.3)" />
            {activeChat ? (
              <>
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={() => setActiveChat(null)}
                >
                  <IconArrowLeft size={14} />
                </ActionIcon>
                <Text fw={600} size="sm" c="dark" truncate>
                  {activeChat.name}
                </Text>
              </>
            ) : (
              <>
                <IconPhone size={15} color="#ff922b" />
                <Title order={5} fz={13} c="gray.8">
                  Friends
                </Title>
              </>
            )}
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray.4"
            onClick={onClose}
          >
            <IconX size={15} />
          </ActionIcon>
        </Group>
      </Box>

      {/* Content */}
      {activeChat ? (
        /* Conversation view */
        <>
          <ScrollArea
            ref={scrollRef}
            style={{ flex: 1, minHeight: 0 }}
            offsetScrollbars
          >
            <Stack gap={6} p={10}>
              {loadingMessages && (
                <Text size="xs" c="dimmed" ta="center" py="md">
                  Loading...
                </Text>
              )}
              {!loadingMessages && messages.length === 0 && (
                <Text size="xs" c="dimmed" ta="center" py="md">
                  No messages yet. Say hi!
                </Text>
              )}
              {messages.map((msg) => {
                const isMine = msg.senderAddress === session.address;
                return (
                  <Box
                    key={msg.id}
                    style={{
                      display: "flex",
                      justifyContent: isMine ? "flex-end" : "flex-start",
                    }}
                  >
                    <Box
                      px={10}
                      py={6}
                      style={{
                        maxWidth: "80%",
                        borderRadius: 12,
                        background: isMine
                          ? "rgba(253,126,20,0.12)"
                          : "rgba(0,0,0,0.04)",
                        borderBottomRightRadius: isMine ? 4 : 12,
                        borderBottomLeftRadius: isMine ? 12 : 4,
                      }}
                    >
                      <Text
                        size="xs"
                        c="dark"
                        style={{ wordBreak: "break-word" }}
                      >
                        {msg.text}
                      </Text>
                      <Text
                        size="xs"
                        c="gray.5"
                        ta={isMine ? "right" : "left"}
                        mt={2}
                      >
                        {new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </ScrollArea>

          {/* Message input */}
          <Box
            p={8}
            style={{
              borderTop: "1px solid rgba(0,0,0,0.06)",
              flexShrink: 0,
            }}
          >
            <TextInput
              placeholder="Type a message..."
              size="sm"
              radius="xl"
              value={dmText}
              onChange={(e) => setDmText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && dmText.trim()) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              rightSection={
                <ActionIcon
                  variant="filled"
                  color="orange"
                  size={26}
                  radius="xl"
                  disabled={!dmText.trim() || sending}
                  onClick={() => void handleSend()}
                >
                  <IconSend size={14} />
                </ActionIcon>
              }
              styles={{
                input: {
                  backgroundColor: "#f1f3f5",
                  border: "1px solid rgba(0,0,0,0.08)",
                  paddingRight: 40,
                },
              }}
            />
          </Box>
        </>
      ) : (
        /* Friends list view */
        <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars>
          <Stack gap={0}>
            {incomingPendingFriends.length > 0 && (
              <Box
                px={12}
                py={8}
                style={{
                  borderBottom: "1px solid rgba(0,0,0,0.04)",
                  background: "rgba(253,126,20,0.04)",
                }}
              >
                <Text size="xs" fw={700} c="orange.8" mb={6}>
                  Friend Requests
                </Text>
                <Stack gap={6}>
                  {incomingPendingFriends.map((friend) => {
                    const requester = friend.userAddress;
                    const requesterName =
                      friend.displayName ||
                      `${requester.slice(0, 8)}...${requester.slice(-4)}`;
                    const rowKey = `${friend.userAddress}:${friend.friendAddress}`;
                    return (
                      <Group
                        key={rowKey}
                        justify="space-between"
                        wrap="nowrap"
                        gap={8}
                      >
                        <Text size="xs" c="dark" truncate style={{ flex: 1 }}>
                          {requesterName}
                        </Text>
                        <Button
                          size="compact-xs"
                          variant="light"
                          color="orange"
                          loading={acceptingFriendKey === rowKey}
                          onClick={() => {
                            setAcceptingFriendKey(rowKey);
                            void onAcceptFriend(friend).finally(() => {
                              setAcceptingFriendKey((current) =>
                                current === rowKey ? null : current
                              );
                            });
                          }}
                        >
                          Accept
                        </Button>
                      </Group>
                    );
                  })}
                </Stack>
              </Box>
            )}
            {sortedFriends.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="xl">
                {incomingPendingFriends.length > 0
                  ? "No accepted friends yet."
                  : "No friends yet."}
              </Text>
            )}
            {sortedFriends.map((f) => {
              const otherAddr =
                f.userAddress === session.address
                  ? f.friendAddress
                  : f.userAddress;
              const presence = presenceMap.get(otherAddr);
              const name =
                f.displayName ||
                `${otherAddr.slice(0, 8)}...${otherAddr.slice(-4)}`;
              const unread = unreadPerFriend[otherAddr] ?? 0;
              return (
                <Box
                  key={otherAddr}
                  px={12}
                  py={8}
                  onClick={() => void openConversation(otherAddr, name)}
                  style={{
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(0,0,0,0.04)",
                    transition: "background 0.15s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "rgba(0,0,0,0.02)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "";
                  }}
                >
                  <Group justify="space-between" wrap="nowrap" gap={8}>
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Group gap={6} wrap="nowrap">
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: presence ? "#40c057" : "#adb5bd",
                            flexShrink: 0,
                          }}
                        />
                        <Text size="sm" fw={600} c="dark" truncate>
                          {name}
                        </Text>
                        {unread > 0 && (
                          <Box
                            style={{
                              minWidth: 18,
                              height: 18,
                              borderRadius: 9,
                              background: "#ff0000",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                              color: "white",
                              fontWeight: 700,
                              lineHeight: 1,
                              padding: "0 5px",
                              flexShrink: 0,
                            }}
                          >
                            {unread > 99 ? "99+" : unread}
                          </Box>
                        )}
                      </Group>
                      <Text size="xs" c="gray.5" truncate ml={14}>
                        {presence
                          ? presence.roomOwnerName
                            ? `in ${presence.roomOwnerName}'s room`
                            : `in ${presence.roomName}`
                          : "Offline"}
                      </Text>
                    </Box>
                    <Group gap={4} wrap="nowrap">
                      {presence && (
                        <Button
                          size="compact-xs"
                          variant="light"
                          color="orange"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVisitRoom(presence.roomId);
                          }}
                        >
                          Visit
                        </Button>
                      )}
                      <IconChevronRight size={14} color="rgba(0,0,0,0.2)" />
                    </Group>
                  </Group>
                </Box>
              );
            })}
          </Stack>
        </ScrollArea>
      )}
    </Paper>
  );
}
